<template src="./template.html"></template>
<style src="./styles.scss" lang="scss"></style>

<script>
import { clone, cloneDeep } from 'lodash'
import { mapActions, mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import moment from 'moment'
import api from '@/api'
import Datepicker from 'vuejs-datepicker'
import { sectionComponentList, getCustomFieldsList } from '@/helper'
import TopLeftTextField from './TopLeftTextField'
import TopRightDataField from './TopRightDataField'
import CancelationOverlay from '@/components/overlays/CancelationOverlay'
import ProfilePageTopSection from '@/views/ProfilePage/variants/profileSections/ProfilePageTopSection'
import children from './ChildrenEditSection'
import topics from './TopicEditSection'
import subscriptions from './SubscriptionEditViewSection'
import FileUploader from '@/components/ui/FileUploader'
import MultiSelectInput from '@/components/ui/MultiSelectInput'
import SingleSelectInput from '@/components/ui/SingleSelectInput'
import EditProfileImageOverlay from '@/components/overlays/EditProfileImageOverlay'

export default {
  name: 'ProfileVariantEditView',
  components: {
    EditProfileImageOverlay,
    FileUploader,
    MultiSelectInput,
    ProfilePageTopSection,
    SingleSelectInput,
    CancelationOverlay,
    children,
    topics,
    'custom-fields': () => import('@/components/modules/CustomFieldEditSection'),
    subscriptions,
    Datepicker,
    TopLeftTextField,
    TopRightDataField,
  },
  props: {
    isOwn: Boolean,
    isAdminUrl: String,
    canEdit: String,
  },
  data() {
    return {
      cancelationType: '',
      children: [],
      confirm_password: '',
      current_password: '',
      customFields: [],
      custom_field_payload: {},
      fakePassword: true,
      files: [],
      fileUploaderConfig: {
        // Should add file config properties here which different from FileUploader's baseConfig settings
      },
      imageUploaderConfig: {
        url: `${process.env.VUE_APP_API_URL}users/1/image`,
      },
      calledOption: null,
      format: 'dd.MM.yyyy',
      isDeliveryAddressDifferent: false,
      newChild: {},
      new_password: '',
      options: {},
      overlayUpload: false,
      payment: null,
      postErrors: [],
      selected: {},
      selected_custom_field: {},
      selected_delivery_address_country: null,
      selected_delivery_address_salutation: null,
      selected_invoice_address_country: null,
      selected_radio_field: true,
      selected_user_salutation: null,
      selected_user_status: null,
      subscription: null,
      updatedBirthday: '',
      topLeftDataFields: [],
      invoiceAddressFields: [],
      deliveryAddressFields: [],
    }
  },
  computed: {
    ...mapState('users', [
      'genderOptions',
      'countryOptions',
      'salutationOptions',
      'deliveryAddress',
      'invoiceAddress',
      'user',
      'userTopics',
      'userChildren',
      'invoiceAddress',
      'deliveryAddress',
      'userCustomFields',
      'statusOptions',
    ]),
    ...mapState('subscriptions', ['billing_data']),
    ...mapFields('users', {
      user_title: 'user.title',
      user_firstname: 'user.firstname',
      user_lastname: 'user.lastname',
      user_email: 'user.email',
      user_business_email: 'user.business_email',
      user_phone: 'user.phone',
      user_business_phone: 'user.business_phone',
      user_birthday: 'user.birthday',
      user_salutation: 'user.gender',
      user_status: 'user.status',
      invoiceAddress_address: 'invoiceAddress.address',
      invoiceAddress_house_number: 'invoiceAddress.house_number',
      invoiceAddress_zip: 'invoiceAddress.zip',
      invoiceAddress_city: 'invoiceAddress.city',
      invoice_address_country: 'invoiceAddress.country',
      deliveryAddress_title: 'deliveryAddress.title',
      delivery_address_country: 'deliveryAddress.country',
      deliveryAddress_firstname: 'deliveryAddress.firstname',
      deliveryAddress_lastname: 'deliveryAddress.lastname',
      deliveryAddress_address: 'deliveryAddress.address',
      deliveryAddress_house_number: 'deliveryAddress.house_number',
      deliveryAddress_zip: 'deliveryAddress.zip',
      deliveryAddress_city: 'deliveryAddress.city',
      deliveryAddress_country: 'deliveryAddress.country',
      delivery_address_salutation: 'deliveryAddress.gender',
    }),
    ...mapState('auth', ['settings']),
    formattedBirthday() {
      return this.user_birthday ? moment(this.user_birthday).format('DD.MM.YYYY') : ''
    },
    editableTopicList() {
      const topics = cloneDeep(this.userTopics)
      topics.shift()
      return topics
    },
    sectionList() {
      let included_profile_sections = []
      if (this.settings && this.settings.profile_options) {
        included_profile_sections = this.settings.profile_options.included_profile_sections
      }
      return sectionComponentList(
        included_profile_sections,
        Object.keys(this.$options.components),
      )
    },
    userStatusText() {
      return this.user.status ? this.user.status.text : ''
    },
    personalDataTitle() {
      return this.isInCustomerProfileOptions('Cards', 'custom-fields') && this.customFields[0] ?
        this.customFields[0].title : this.$t('profile.own_profile.personal_data.title')
    },
  },
  watch: {
    'options'(val) {
      if (this.calledOption && val[this.calledOption]) {
        const checkOptions = setInterval(
          () => {
            const ref = document.querySelector(`#select-dropdown-${this.calledOption} button`)
            if (ref) {
              ref.click()
              clearInterval(checkOptions)
              setTimeout(
                () => {
                  this.calledOption = null
                },
                200,
              )
            }
          },
          500,
        )
      }
    },
  },
  created() {
    if (this.isInCustomerProfileOptions('Cards', 'custom-fields')
        || this.isInCustomerProfileOptions('Sections', 'custom-fields')
    ) {
      this.customFields = cloneDeep(this.userCustomFields)
      getCustomFieldsList(this.customFields, this.custom_field_payload)
      this.getStatusOptions()
    } else {
      this.getCountryOptions()
      this.getSalutationOptions()
    }
    this.initUpdateSelectValue()
    this.children = cloneDeep(this.userChildren)
    this.getGenderOptions()
    this.setTopLeftDataFields()
    this.setInvoiceAddressFields()
    this.setDeliveryAddressFields()
  },
  methods: {
    ...mapActions('users', [
      'getGenderOptions',
      'updateDataTopic',
      'getCountryOptions',
      'getSalutationOptions',
      'saveChildrenData',
      'getChildrensData',
      'editProfile',
      'getUser',
      'updateAddress',
      'updateTopics',
      'updateRadioDataTopic',
      'updateSubscriptionsOptions',
      'getCustomFields',
      'saveCustomFields',
      'getStatusOptions',
      'deleteUser',
    ]),
    ...mapActions('auth', [ 'changePassword' ]),
    ...mapActions('subscriptions', [
      'removeSubscription',
      'getSubscriptionData',
    ]),
    ...mapActions('posts', ['deleteFile']),
    setCancelationType(type) {
      this.cancelationType = type
    },
    addChild() {
      this.children.push(clone(this.newChild))
    },
    onSubmit() {
      this.$refs.profileForm.validate().then(_ => {
        if (!this.$refs.profileForm.flags.valid || this.$refs.profileForm.flags.invalid) {
          // exit with general error if form isn't valid
          this.postErrors = [ this.$t('errors.messages.required_fields') ]
          return false
        }

        this.postErrors = []
        const userData = {
          title: this.user_title,
          firstname: this.user_firstname,
          lastname: this.user_lastname,
          email: this.user_email,
          business_email: this.user_business_email,
          phone: this.user_phone,
          business_phone: this.user_business_phone,
          gender: this.selected_user_salutation,
          status: this.selected_user_status,
          birthday: this.updatedBirthday || this.user.birthday,
          newsletter_subscriptions: this.user.newsletter_subscriptions,
        }

        const userDataPromise = this.editProfile(userData).then(_ =>{
          this.getUser(this.$route.params.id)
        })

        let invoiceAddressPromise = null
        let deliveryAddressPromise = null
        let userTopicPromise = null
        let saveChildrenPromise = null
        let saveCustomPromise = null

        let changePasswordPromise
        if (this.current_password && this.new_password && this.confirm_password) {
          changePasswordPromise = this.changePassword({
            current_password: this.current_password,
            new_password: this.new_password,
            new_password_confirm: this.confirm_password,
          })
        }

        if (this.isInCustomerProfileOptions('Cards', 'custom-fields') || this.isInCustomerProfileOptions('Sections', 'custom-fields')
      ) {
          saveCustomPromise = this.saveCustomFields({ id: this.$route.params.id, customFields: { custom_profile_fields: this.custom_field_payload } }).then(_=>{
            this.getCustomFields(this.$route.params.id)
          })
        } else {
          invoiceAddressPromise = this.updateAddress({ addressType: 'invoice_address', address: this.invoiceAddress, id: this.$route.params.id })
          if (this.isDeliveryAddressDifferent) {
            deliveryAddressPromise = this.updateAddress({ addressType: 'delivery_address', address: this.deliveryAddress, id: this.$route.params.id })
          }
          userTopicPromise = this.updateTopics(this.$route.params.id)
          saveChildrenPromise = this.saveChildrenData({ id: this.$route.params.id, children: this.children }).then(_ => {
            this.getChildrensData(this.$route.params.id)
          })
        }

        Promise.all([
          userDataPromise,
          invoiceAddressPromise,
          deliveryAddressPromise,
          userTopicPromise,
          changePasswordPromise,
          saveChildrenPromise,
          saveCustomPromise,
        ]).then(_ => {
          this.$emit('resetEditView')
          this.$emit('showStatusMessage')
        }).catch(errors => {
          this.postErrors = errors
        })
      })
    },
    onUpdateGender(id, args) {
      this.onUpdateResults(id, args)
      if (this.children[id]) {
        this.children[id].gender = args.key
      }
    },
    onUpdateResults(type, args) {
      this.$set(this.selected, type, args)

      this[`selected_${type}`] = args || null
      this[type] = args || null
    },
    updateOption(topic_id, option_id, args) {
      this.updateDataTopic({ topic_id, option_id, args })
    },
    updateRadioOption(topic_id, option_id) {
      this.updateRadioDataTopic({ topic_id, option_id })
    },
    setOverlayUpload(value) {
      this.overlayUpload = value
    },
    setBirthday(e) {
      this.updatedBirthday = moment(moment(e, 'DD.MM.YYYY')).format()
    },
    initUpdateSelectValue() {
      this.selected_invoice_address_country = this.invoiceAddress && this.invoiceAddress.country || null
      this.selected_delivery_address_country = this.deliveryAddress && this.deliveryAddress.country || null
      this.selected_delivery_address_salutation = this.deliveryAddress && this.deliveryAddress.gender || null
      this.selected_user_salutation = this.user && this.user.gender || null
      this.selected_user_status = this.user && this.user.status || null
    },
    updateNewsletterSubscriptions(key, args) {
      this.updateSubscriptionsOptions({ key, args })
    },
    populateDropdown(id) {
      this.calledOption = id
      api.items.getItems(`profile_field_topics.${id}`).then(({ data }) => {
        const optionsData = { ...this.options }
        optionsData[id] = data
        this.options = optionsData
      })
    },
    getSelectedOptions(obj, id) {
      const resultsData = {}
      resultsData[id] = obj
      return this.selected_custom_field && this.selected_custom_field[id]
        ? this.selected_custom_field[id]
        : resultsData[id]
    },
    onUpdateOptions(id, args, isSingleSlect) {
      const optionsData = { ...this.selected_custom_field }
      optionsData[id] = args
      this.selected_custom_field = optionsData
      this.custom_field_payload[id] = isSingleSlect ? [args] : args
    },
    changeFiles(id, file) {
      const optionsData = { ...this.selected_custom_field }
      optionsData[id] = { file }
      this.selected_custom_field = optionsData
      this.custom_field_payload[id] = { file: [
        ...this.custom_field_payload[id].file,
        ...file,
      ].filter((value, index, array)=>{
        return array.indexOf(value) === index
      }) }
    },
    removeFile(fileId) {
      this.deleteFile(fileId).then(({ data }) => {
        this.getCustomFields(this.$route.params.id).then(_ => {
          this.customFields = cloneDeep(this.userCustomFields)
        })
      })
    },
    closeCancelationOverlay() {
      this.cancelationType = ''
    },
    confirmCancelation(type) {
      if (type === 'cancel_account') {
        this.deleteUser(this.$route.params.id).then(_ => {
          this.closeCancelationOverlay()
        })
      } else {
        this.removeSubscription(this.$route.params.id).then(async _ => {
          await this.getSubscriptionData()
          this.closeCancelationOverlay()
        })
      }
    },
    getSectionData(section) {
      let data = {}
      switch (section) {
        case 'children':
          data = {
            children: this.children,
            genderOptions: this.genderOptions,
            format: this.format,
            selected: this.selected,
          }
          break
        case 'custom-fields':
          data = {
            customFields: this.customFields,
            options: this.options,
            fileUploaderConfig: this.fileUploaderConfig,
            selectedCustomField: this.selected_custom_field,
            fieldType: 'profile',
          }
          break
        case 'subscriptions':
          data = {
            billingData: this.billing_data,
            cancelationType: this.cancelationType,
          }
          break
        case 'topics':
          data = {
            editableTopicList: this.editableTopicList,
            selectedRadioField: this.selected_radio_field,
          }
      }
      return data
    },
    getSectionMethods(section) {
      let methods = {}
      switch (section) {
        case 'children':
          methods = {
            onUpdateGender: this.onUpdateGender,
            addChild: this.addChild,
          }
          break
        case 'custom-fields':
          methods = {
            onUpdateOptions: this.onUpdateOptions,
            populateDropdown: this.populateDropdown,
            removeFile: this.removeFile,
            changeFiles: this.changeFiles,
          }
          break
        case 'subscriptions':
          methods = {
            setCancelationType: this.setCancelationType,
          }
          break
        case 'topics':
          methods = {
            updateOption: this.updateOption,
            updateRadioOption: this.updateRadioOption,
          }
      }
      return methods
    },
    setTopLeftDataFields() {
      this.topLeftDataFields = [
        {
          condition: true,
          key: 'user_firstname',
          label: this.$t('profile.own_profile_edit.personal_data.first_name'),
          rules: 'required',
          type: 'text',
          validate: true,
        },
        {
          condition: true,
          key: 'user_lastname',
          label: this.$t('profile.own_profile_edit.personal_data.last_name'),
          rules: 'required',
          type: 'text',
          validate: true,
        },
        {
          condition: this.isInCustomerProfileOptions('Cards', 'business_email'),
          key: 'user_business_email',
          label: this.$t('profile.own_profile_edit.personal_data.business_email'),
          rules: 'email',
          type: 'email',
          validate: true,
        },
        {
          condition: true,
          key: 'user_email',
          label: this.$t('profile.own_profile_edit.personal_data.email'),
          rules: 'email',
          type: 'email',
          validate: true,
        },
        {
          condition: this.isInCustomerProfileOptions('Cards', 'business_phone'),
          key: 'user_business_phone',
          label: this.$t('profile.own_profile_edit.personal_data.business_phone'),
          type: 'text',
          validate: false,
        },
        {
          condition: true,
          key: 'user_phone',
          label: this.$t('profile.own_profile_edit.personal_data.phone'),
          type: 'text',
          validate: false,
        },
      ]
    },
    setInvoiceAddressFields() {
      this.invoiceAddressFields = [
        {
          key: 'invoiceAddress_address',
          label: this.$t('profile.own_profile_edit.personal_data.billing_address.fields.street'),
          required: true,
          md: 9,
        },
        {
          key: 'invoiceAddress_house_number',
          label: this.$t('profile.own_profile_edit.personal_data.billing_address.fields.number'),
          required: true,
          md: 3,
        },
        {
          key: 'invoiceAddress_zip',
          label: this.$t('profile.own_profile_edit.personal_data.billing_address.fields.plz'),
          required: true,
          md: 4,
        },
        {
          key: 'invoiceAddress_city',
          label: this.$t('profile.own_profile_edit.personal_data.billing_address.fields.city'),
          required: true,
          md: 8,
        },
      ]
    },
    setDeliveryAddressFields() {
      this.deliveryAddressFields = [
        {
          key: 'deliveryAddress_firstname',
          label: this.$t('profile.own_profile_edit.personal_data.delivery_address.fields.first_name'),
          required: true,
          md: 6,
        },
        {
          key: 'deliveryAddress_lastname',
          label: this.$t('profile.own_profile_edit.personal_data.delivery_address.fields.last_name'),
          required: true,
          md: 6,
        },
        {
          key: 'deliveryAddress_address',
          label: this.$t('profile.own_profile_edit.personal_data.delivery_address.fields.street'),
          required: true,
          md: 6,
        },
        {
          key: 'deliveryAddress_house_number',
          label: this.$t('profile.own_profile_edit.personal_data.delivery_address.fields.number'),
          required: true,
          md: 6,
        },
        {
          key: 'deliveryAddress_zip',
          label: this.$t('profile.own_profile_edit.personal_data.delivery_address.fields.plz'),
          required: true,
          md: 6,
        },
        {
          key: 'deliveryAddress_city',
          label: this.$t('profile.own_profile_edit.personal_data.delivery_address.fields.city'),
          required: true,
          md: 6,
        },
      ]
    },
  },
}
</script>
